/* ==========================================================================
   FioAFio Theme – JavaScript
   ========================================================================== */

'use strict';

/* --------------------------------------------------------------------------
   Produtos relacionados (recomendação automática do Shopify)
   -------------------------------------------------------------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        // A rota de recomendações retorna o HTML da seção direto (sem a tag
        // <product-recommendations>, que só existe na página que faz a busca).
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContent = doc.querySelector('.product-related');
        if (newContent) {
          this.innerHTML = newContent.outerHTML;
        } else {
          this.remove();
        }
      })
      .catch(() => this.remove());
  }
}
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/* --------------------------------------------------------------------------
   Utilitários
   -------------------------------------------------------------------------- */

/**
 * Faz requisição à Cart API do Shopify
 * @param {string} endpoint - Ex: '/cart/add.js'
 * @param {object} data     - Body da requisição
 */
async function cartFetch(endpoint, data = null) {
  const opts = {
    method: data ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  };
  if (data) opts.body = JSON.stringify(data);
  const res = await fetch(endpoint, opts);
  if (!res.ok) throw new Error(`Cart API error: ${res.status}`);
  return res.json();
}

/**
 * Atualiza o contador de itens do carrinho no header
 */
async function updateCartCount() {
  try {
    const cart = await cartFetch('/cart.js');
    document.querySelectorAll('.cart-count').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.classList.toggle('d-none', cart.item_count === 0);
    });
    return cart;
  } catch (e) {
    console.error('Erro ao atualizar carrinho:', e);
  }
}

/**
 * Busca o HTML atualizado da seção cart-drawer e substitui no DOM
 */
async function refreshCartDrawer() {
  try {
    const url = `${window.location.pathname}?section_id=cart-drawer`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Cart drawer refresh error: ${res.status}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.getElementById('cartDrawer');
    const currentDrawer = document.getElementById('cartDrawer');
    if (newDrawer && currentDrawer) {
      currentDrawer.innerHTML = newDrawer.innerHTML;
    }
  } catch (e) {
    console.error('Erro ao atualizar cart drawer:', e);
  }
}

/**
 * Abre o painel lateral do carrinho (Bootstrap Offcanvas)
 */
function openCartDrawer() {
  const el = document.getElementById('cartDrawer');
  if (!el || typeof bootstrap === 'undefined') return;
  bootstrap.Offcanvas.getOrCreateInstance(el).show();
}

/* --------------------------------------------------------------------------
   Controle de quantidade (+/-)
   -------------------------------------------------------------------------- */
function initQuantityControls() {
  document.addEventListener('click', (e) => {

    // Página de produto
    if (e.target.matches('.qty-minus, .qty-plus')) {
      const btn    = e.target;
      const isPlus = btn.classList.contains('qty-plus');
      const input  = btn.closest('.quantity-selector')?.querySelector('input[type="number"]')
                  || btn.closest('.qty-group')?.querySelector('.qty-input');
      if (!input) return;

      const current = parseInt(input.value, 10) || 1;
      const next    = isPlus ? current + 1 : Math.max(0, current - 1);

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

  });
}

/* --------------------------------------------------------------------------
   Seleção de variantes (PDP)
   -------------------------------------------------------------------------- */
function initVariantSelector() {
  const variantJsonEl = document.getElementById('product-variants-json');
  if (!variantJsonEl) return;

  const variants  = JSON.parse(variantJsonEl.textContent);
  const form      = document.getElementById('product-form');
  const priceEl   = document.getElementById('product-price');
  const variantId = document.getElementById('variant-id');

  // Mapa "id clássico da imagem" -> posição na galeria (product.media pode ter uma
  // numeração de id diferente da usada por variant.featured_image, então guardamos
  // a posição em vez de tentar comparar ids diretamente).
  const imagePositionsEl = document.getElementById('product-image-positions-json');
  const imagePositions = imagePositionsEl ? JSON.parse(imagePositionsEl.textContent) : {};

  // Estado das opções selecionadas
  const selected = {};
  document.querySelectorAll('.variant-btn').forEach((btn) => {
    const name = btn.dataset.optionName;
    if (!selected[name]) selected[name] = btn.dataset.optionValue;
  });
  updateVariantButtonsAvailability();

  function findVariant() {
    return variants.find((v) =>
      v.options.every((opt, i) => {
        const key = Object.keys(selected)[i];
        return selected[key] === opt;
      })
    );
  }

  // Verifica se existe uma variante disponível (em estoque) combinando o valor
  // testado com as demais opções já selecionadas.
  function isCombinationAvailable(overrides) {
    const optionNames = Object.keys(selected);
    const test = { ...selected, ...overrides };
    return variants.some((v) =>
      v.available && v.options.every((opt, i) => test[optionNames[i]] === opt)
    );
  }

  function updateVariantButtonsAvailability() {
    document.querySelectorAll('.variant-btn').forEach((btn) => {
      const name = btn.dataset.optionName;
      const value = btn.dataset.optionValue;
      const available = isCombinationAvailable({ [name]: value });
      btn.classList.toggle('variant-btn--sold-out', !available);
      btn.disabled = !available;
    });
  }

  function formatMoney(cents) {
    return Shopify.formatMoney
      ? Shopify.formatMoney(cents, window.moneyFormat || '${{amount}}')
      : (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  function updateUI(variant) {
    if (!variant) return;

    // Preço
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('fs-4', !!onSale);
      priceEl.classList.toggle('txt-pink', !!onSale);
      priceEl.classList.toggle('me-2', !!onSale);

      const compareEl = document.getElementById('product-compare-price');
      const badgeEl = document.getElementById('product-discount-badge');
      if (compareEl) {
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      }
      if (badgeEl) {
        badgeEl.hidden = !onSale;
        badgeEl.textContent = onSale
          ? '-' + Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) + '%'
          : '';
      }
    }

    // Parcelamento (6x)
    const installmentEl = document.getElementById('product-installment-value');
    if (installmentEl) {
      installmentEl.textContent = formatMoney(Math.round(variant.price / 6));
    }

    // ID da variante no form
    if (variantId) variantId.value = variant.id;

    // Botão de compra
    const addBtn = form?.querySelector('.add-to-cart-btn');
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? 'Adicionar ao carrinho' : 'Produto esgotado';
    }

    // Foto da variante (ex: cor) na galeria, se houver uma associada
    if (variant.featured_image) {
      const classicIndex = imagePositions[variant.featured_image.id];
      const galleryEl = document.getElementById('productGallery');
      if (classicIndex !== undefined && galleryEl && typeof bootstrap !== 'undefined') {
        const items = Array.from(galleryEl.querySelectorAll('.carousel-item'));
        const targetIndex = items.findIndex(
          (item) => parseInt(item.dataset.classicImageIndex, 10) === classicIndex
        );
        if (targetIndex > -1) {
          bootstrap.Carousel.getOrCreateInstance(galleryEl).to(targetIndex);
        }
      }
    }
  }

  document.addEventListener('click', (e) => {
    if (!e.target.matches('.variant-btn')) return;

    const btn   = e.target;
    const name  = btn.dataset.optionName;
    const value = btn.dataset.optionValue;

    // Atualiza estado
    selected[name] = value;

    // Atualiza botões ativos
    document.querySelectorAll(`.variant-btn[data-option-name="${name}"]`).forEach((b) => {
      b.classList.toggle('active', b.dataset.optionValue === value);
    });

    // Label do valor selecionado
    document.querySelectorAll('[data-selected-label]').forEach((label) => {
      if (label.dataset.selectedLabel === name) label.textContent = value;
    });

    // Atualiza quais botões estão disponíveis (esgotados ficam desabilitados e com X)
    updateVariantButtonsAvailability();

    // Atualiza variante
    const variant = findVariant();
    if (variant) {
      updateUI(variant);
      // Atualiza URL sem recarregar
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  });
}

/* --------------------------------------------------------------------------
   Formulário Adicionar ao Carrinho (AJAX)
   -------------------------------------------------------------------------- */
function initCartForm() {
  const form = document.getElementById('product-form');
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const btn = form.querySelector('[name="add"]');
    const originalText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Adicionando...';

    try {
      const data = {
        id:       parseInt(form.querySelector('[name="id"]').value, 10),
        quantity: parseInt(form.querySelector('[name="quantity"]').value, 10) || 1,
      };
      await cartFetch('/cart/add.js', { items: [data] });
      await updateCartCount();
      await refreshCartDrawer();
      openCartDrawer();

      btn.innerHTML = originalText;
      btn.disabled  = false;
    } catch (err) {
      console.error(err);
      btn.innerHTML = 'Erro – tente novamente';
      btn.disabled  = false;
    }
  });
}

/* --------------------------------------------------------------------------
   Quick Add (cards de produto)
   -------------------------------------------------------------------------- */
function initQuickAdd() {
  document.addEventListener('submit', async (e) => {
    if (!e.target.closest('.product-card form')) return;
    e.preventDefault();

    const form = e.target;
    const btn  = form.querySelector('button[type="submit"]');
    const id   = parseInt(form.querySelector('[name="id"]').value, 10);

    btn.disabled = true;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';

    try {
      await cartFetch('/cart/add.js', { items: [{ id, quantity: 1 }] });
      await updateCartCount();
      await refreshCartDrawer();
      openCartDrawer();

      btn.innerHTML = '<i class="bi bi-bag-plus me-1"></i>Adicionar';
      btn.disabled  = false;
    } catch (err) {
      console.error(err);
      btn.innerHTML = 'Erro';
      btn.disabled  = false;
    }
  });
}

/* --------------------------------------------------------------------------
   Atualização de quantidade no carrinho (AJAX)
   -------------------------------------------------------------------------- */
function initCartUpdates() {
  const cartForm = document.getElementById('cart-form');
  if (!cartForm) return;

  // Debounce helper
  const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  async function updateItem(key, quantity) {
    try {
      const cart = await cartFetch('/cart/change.js', { id: key, quantity });
      updateCartCount();

      if (quantity === 0) {
        document.getElementById(`cart-item-${key}`)?.remove();
      }

      // Atualiza total na página
      const totalEl = document.querySelector('.cart-summary .fw-bold.fs-5 span:last-child');
      if (totalEl) {
        totalEl.textContent = (cart.total_price / 100).toLocaleString('pt-BR', {
          style: 'currency', currency: 'BRL',
        });
      }

      if (cart.item_count === 0) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Erro ao atualizar item:', err);
    }
  }

  const debouncedUpdate = debounce(updateItem, 500);

  cartForm.addEventListener('click', (e) => {
    const btn = e.target.closest('.qty-minus, .qty-plus, .cart-remove-btn');
    if (!btn) return;

    const key = btn.dataset.key;

    if (btn.classList.contains('cart-remove-btn')) {
      e.preventDefault();
      updateItem(key, 0);
      return;
    }

    const input   = btn.closest('.qty-group')?.querySelector('.qty-input');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    const next    = btn.classList.contains('qty-plus') ? current + 1 : Math.max(0, current - 1);
    input.value   = next;
    debouncedUpdate(key, next);
  });

  cartForm.addEventListener('change', (e) => {
    if (!e.target.matches('.qty-input')) return;
    const key = e.target.dataset.key;
    const qty = parseInt(e.target.value, 10);
    debouncedUpdate(key, isNaN(qty) ? 0 : qty);
  });
}

/* --------------------------------------------------------------------------
   Atualização de quantidade no cart drawer (AJAX)
   -------------------------------------------------------------------------- */
function initCartDrawerUpdates() {
  const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  async function updateDrawerItem(key, quantity) {
    try {
      await cartFetch('/cart/change.js', { id: key, quantity });
      await updateCartCount();
      await refreshCartDrawer();
    } catch (err) {
      console.error('Erro ao atualizar item do carrinho:', err);
    }
  }

  const debouncedDrawerUpdate = debounce(updateDrawerItem, 500);

  // Remover item: ação imediata, própria do drawer
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#cartDrawer')) return;
    const btn = e.target.closest('.cart-remove-btn');
    if (!btn) return;
    e.preventDefault();
    updateDrawerItem(btn.dataset.key, 0);
  });

  // +/- de quantidade: o incremento do input já é feito pelo initQuantityControls
  // (listener global de .qty-minus/.qty-plus), aqui só reagimos ao "change" do input.
  document.addEventListener('change', (e) => {
    if (!e.target.closest('#cartDrawer')) return;
    if (!e.target.matches('.qty-input')) return;
    const key = e.target.dataset.key;
    const qty = parseInt(e.target.value, 10);
    debouncedDrawerUpdate(key, isNaN(qty) ? 0 : qty);
  });
}

/* --------------------------------------------------------------------------
   Ordenação de coleção
   -------------------------------------------------------------------------- */
function initCollectionSort() {
  const sortSelect = document.querySelector('.sort-by');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

/* --------------------------------------------------------------------------
   Filtros de coleção (checkbox → navegação)
   -------------------------------------------------------------------------- */
function initCollectionFilters() {
  document.querySelectorAll('.filter-checkbox').forEach((cb) => {
    cb.addEventListener('change', () => {
      window.location.href = cb.dataset.href;
    });
  });
}

/* --------------------------------------------------------------------------
   Header com classe "scrolled" ao rolar a página
   -------------------------------------------------------------------------- */
function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const threshold = 160;
  let ticking = false;

  function update() {
    header.classList.toggle('scrolled', window.scrollY > threshold);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }, { passive: true });

  update(); // estado inicial (ex: página recarregada já rolada)
}

/* --------------------------------------------------------------------------
   Rotação dos avisos da barra do header (frete grátis + avisos extras)
   -------------------------------------------------------------------------- */
function initHeaderAnnouncements() {
  const wrap = document.getElementById('headerAnnouncements');
  if (!wrap) return;

  const slides = wrap.querySelectorAll('.header-free-shipping-slide');
  if (slides.length <= 1) return;

  const interval = parseInt(wrap.dataset.interval, 10) || 5000;
  let current = 0;

  setInterval(() => {
    slides[current].classList.remove('active');
    current = (current + 1) % slides.length;
    slides[current].classList.add('active');
  }, interval);
}

/* --------------------------------------------------------------------------
   Abrir dropdowns do menu ao passar o mouse (desktop)
   -------------------------------------------------------------------------- */
function initDropdownHover() {
  if (!window.matchMedia('(hover: hover)').matches) return;
  if (typeof bootstrap === 'undefined') return;

  document.querySelectorAll('.nav-item.dropdown').forEach((item) => {
    const toggle = item.querySelector('[data-bs-toggle="dropdown"]');
    if (!toggle) return;

    const dropdown = bootstrap.Dropdown.getOrCreateInstance(toggle);
    let closeTimer;

    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      dropdown.show();
    });

    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => dropdown.hide(), 150);
    });
  });
}

function initProductGalleryThumbs() {
  const galleryEl = document.getElementById('productGallery');
  const thumbs = Array.from(document.querySelectorAll('.product-thumb'));
  if (!galleryEl || !thumbs.length) return;

  // O Bootstrap só destaca automaticamente elementos dentro de .carousel-indicators;
  // como as miniaturas ficam fora do carrossel, sincronizamos manualmente aqui.
  galleryEl.addEventListener('slid.bs.carousel', (e) => {
    thumbs.forEach((thumb) => {
      const index = parseInt(thumb.getAttribute('data-bs-slide-to'), 10);
      thumb.classList.toggle('active', index === e.to);
    });
  });

  initProductGalleryVideoAutoplay(galleryEl);
}

// Ao chegar num slide de vídeo, toca automaticamente sem som; ao sair, pausa.
function initProductGalleryVideoAutoplay(galleryEl) {
  const items = Array.from(galleryEl.querySelectorAll('.carousel-item'));

  const stop = (item) => {
    item.querySelectorAll('video').forEach((v) => v.pause());
    item.querySelectorAll('iframe').forEach((f) => {
      if (f.dataset.origSrc) f.src = f.dataset.origSrc;
    });
  };

  const play = (item) => {
    item.querySelectorAll('video').forEach((v) => {
      v.muted = true;
      v.setAttribute('playsinline', '');
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    });
    item.querySelectorAll('iframe').forEach((f) => {
      if (!f.dataset.origSrc) f.dataset.origSrc = f.src;
      try {
        const url = new URL(f.dataset.origSrc, window.location.href);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('muted', '1');
        f.allow = (f.allow ? f.allow + '; ' : '') + 'autoplay';
        f.src = url.toString();
      } catch (err) {}
    });
  };

  // Ícone de lupa: amplia a imagem do slide ativo (some quando o slide é um vídeo)
  const zoomBtn = galleryEl.querySelector('.product-zoom-btn');
  const syncZoomBtn = () => {
    if (!zoomBtn) return;
    const active = galleryEl.querySelector('.carousel-item.active .product-zoomable');
    zoomBtn.classList.toggle('is-hidden', !active);
  };
  if (zoomBtn) {
    zoomBtn.addEventListener('click', () => {
      const active = galleryEl.querySelector('.carousel-item.active .product-zoomable');
      if (active) active.click();
    });
    galleryEl.addEventListener('slid.bs.carousel', syncZoomBtn);
    syncZoomBtn();
  }

  const badge = galleryEl.querySelector('.product-video-badge');
  if (badge) {
    const videoIndex = parseInt(badge.dataset.videoIndex, 10);
    badge.addEventListener('click', () => {
      const carousel = bootstrap.Carousel.getOrCreateInstance(galleryEl);
      carousel.to(videoIndex);
      galleryEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    // Flutuante: aparece após 3s e some após 20s da abertura da página
    setTimeout(() => badge.classList.remove('is-waiting'), 3000);
    setTimeout(() => badge.classList.add('is-expired'), 20000);
  }

  galleryEl.addEventListener('slid.bs.carousel', (e) => {
    items.forEach((item, i) => { if (i !== e.to) stop(item); });
    if (items[e.to]) play(items[e.to]);
    // Esconde o selo enquanto o vídeo está na tela
    if (badge) badge.classList.toggle('is-hidden', e.to === parseInt(badge.dataset.videoIndex, 10));
  });
}

// Kit: abre o produto do kit num modal (sem sair da página)
function initKitQuickview() {
  const modalEl = document.getElementById('kitQuickviewModal');
  const body = document.getElementById('kitQuickviewBody');
  if (!modalEl || !body || typeof bootstrap === 'undefined') return;

  // Evita problemas de z-index/posição: o modal vai para o <body>
  document.body.appendChild(modalEl);
  const modal = bootstrap.Modal.getOrCreateInstance(modalEl);

  document.addEventListener('click', async (e) => {
    const link = e.target.closest('.js-kit-quickview');
    if (!link) return;
    // Ctrl/Cmd/botão do meio mantêm o comportamento normal (abrir em nova aba)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();

    body.innerHTML = '<div class="text-center py-5"><div class="spinner-border" role="status"></div></div>';
    modal.show();

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', 'kit-quickview');
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error('HTTP ' + res.status);
      body.innerHTML = await res.text();
    } catch (err) {
      body.innerHTML = `<p class="text-center py-4">Não foi possível carregar o produto. <a href="${link.href}">Abrir página do produto</a></p>`;
    }
  });

  // Troca a imagem principal pelas miniaturas do modal
  body.addEventListener('click', (e) => {
    const thumb = e.target.closest('.kit-quickview-thumb');
    if (!thumb) return;
    const main = body.querySelector('#kitQuickviewMain');
    if (main) main.src = thumb.dataset.full;
  });
}

function initProductGalleryZoom() {
  const modalEl = document.getElementById('productZoomModal');
  const zoomImage = document.getElementById('productZoomImage');
  const zoomables = Array.from(document.querySelectorAll('.product-zoomable'));

  if (!modalEl || !zoomImage || !zoomables.length || typeof bootstrap === 'undefined') return;

  // `images`/`carouselIndexes` só incluem itens do tipo imagem (vídeos ficam de fora do zoom).
  // carouselIndexes[i] guarda a posição REAL do slide no carrossel (pode ter vídeos no meio),
  // enquanto `currentIndex` navega só entre as fotos (0..n-1), sem furos.
  const images = zoomables.map((el) => el.getAttribute('data-zoom-src'));
  const carouselIndexes = zoomables.map((el) => parseInt(el.getAttribute('data-zoom-index'), 10) || 0);
  const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
  const prevBtn = modalEl.querySelector('.product-zoom-prev');
  const nextBtn = modalEl.querySelector('.product-zoom-next');
  const galleryEl = document.getElementById('productGallery');
  const carousel = galleryEl ? bootstrap.Carousel.getOrCreateInstance(galleryEl) : null;

  let currentIndex = 0;

  function showZoom(index) {
    currentIndex = (index + images.length) % images.length;
    zoomImage.src = images[currentIndex];
  }

  zoomables.forEach((el, i) => {
    el.addEventListener('click', () => {
      showZoom(i);
      modal.show();
    });
  });

  if (prevBtn) prevBtn.addEventListener('click', () => showZoom(currentIndex - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => showZoom(currentIndex + 1));

  modalEl.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') showZoom(currentIndex - 1);
    if (e.key === 'ArrowRight') showZoom(currentIndex + 1);
  });

  // Ao fechar o zoom, sincroniza o carrossel principal com a imagem vista por último
  // (usando a posição real no carrossel, já que pode haver vídeos entre as fotos).
  modalEl.addEventListener('hidden.bs.modal', () => {
    if (carousel) carousel.to(carouselIndexes[currentIndex]);
  });
}

function initProductTabsAccordion() {
  const tabsList = document.getElementById('productTabs');
  const tabsContent = document.getElementById('productTabsContent');
  if (!tabsList || !tabsContent) return;

  const mq = window.matchMedia('(max-width: 767.98px)');
  let isMobileLayout = false;

  // Move cada painel para dentro do <li> do próprio botão,
  // assim o conteúdo abre logo abaixo da aba clicada (efeito acordeão).
  function moveToMobile() {
    tabsList.querySelectorAll('.nav-item').forEach((li) => {
      const btn = li.querySelector('.nav-link');
      const targetSelector = btn && btn.getAttribute('data-bs-target');
      const pane = targetSelector && document.querySelector(targetSelector);
      if (pane) li.appendChild(pane);
    });
  }

  // Devolve os painéis para a área compartilhada (comportamento normal de abas).
  function moveToDesktop() {
    tabsList.querySelectorAll('.nav-item .tab-pane').forEach((pane) => {
      tabsContent.appendChild(pane);
    });
  }

  function applyLayout(matchesMobile) {
    if (matchesMobile && !isMobileLayout) {
      moveToMobile();
      isMobileLayout = true;
    } else if (!matchesMobile && isMobileLayout) {
      moveToDesktop();
      isMobileLayout = false;
    }
  }

  applyLayout(mq.matches);
  mq.addEventListener('change', (e) => applyLayout(e.matches));
}

/* --------------------------------------------------------------------------
   Init
   -------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  updateCartCount();
  initQuantityControls();
  initVariantSelector();
  initCartForm();
  initQuickAdd();
  initCartUpdates();
  initCartDrawerUpdates();
  initCollectionSort();
  initCollectionFilters();
  initHeaderScroll();
  initHeaderAnnouncements();
  initDropdownHover();
  initProductTabsAccordion();
  initProductGalleryThumbs();
  initKitQuickview();
  initProductGalleryZoom();
});
